import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IonAccordionGroup, IonModal, ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import {
    ApplicationFacade,
    JourneyDirection,
    OpenTripPlannerFacade,
    OpenTripPlannerService,
    Route,
    SearchService
} from '@ridango/components';
import { AppStartupService, BaseComponent, DepartureArrivalTime } from '@ridango/core';
import { takeUntil } from 'rxjs/operators';
import { JourneyPlannerFilterModalComponent } from '../journey-planner-filter-modal/journey-planner-filter-modal.component';
import Env from '@ridango/core/env';
import {
    getDefaultValue,
    JourneyPlannerFilter
} from '@ridango/components/lib/open-trip-planner/journey-planner-filter.model';

import { queryParameter } from '@ridango/components/lib/open-trip-planner/open-trip-planner.model';
import { Temporal } from '@js-temporal/polyfill';
import { AnalyticsService } from '@ridango/core/lib/services/analytics.service';
import { TrackedEventCategory } from '@ridango/core/lib/enums/analytics/tracked-event-categories.enum';
import { TrackedEventAction } from '@ridango/core/lib/enums/analytics/tracked-event-action.enum';
import { JourneyPlannerFacade } from '@ridango/components/lib/journey-planner';

@Component({
    selector: 'rwidget-journey-planner-search',
    templateUrl: './journey-planner-search.component.html',
    styleUrls: ['./journey-planner-search.component.scss']
})
export class JourneyPlannerSearchComponent extends BaseComponent implements OnInit, OnDestroy {
    @ViewChild('accordionGroup', { static: true }) accordionGroup: IonAccordionGroup;
    @ViewChild(IonModal) timeModal: IonModal;

    @Input() filterDepartTime: DepartureArrivalTime;
    @Output() selectTime = new EventEmitter<string>();

    date: string;
    time: string;
    minDate: string;
    routes: Route[];
    modalTypeEnum = JourneyDirection;

    filter: JourneyPlannerFilter = getDefaultValue(this.env.JOURNEY_PLANNER?.FILTERS);

    highContrast$ = this.applicationFacade.highContrast$;
    assetsPath$ = this.applicationFacade.assetsPath$;
    routes$ = this.openTripPlannerFacade.routes$;
    search$ = this.openTripPlannerFacade.search$;

    minDateForFilter: string;
    maxDateForFilter: string;
    hasOriginSearch: boolean;
    hasDestinationSearch: boolean;
    routeFiltersHaveChanged: boolean;
    isHighContrast = false;

    filterButtonClass = '';
    filterIconClass = '';

    private readonly FILTER_BUTTON_HIGH_CONTRAST_CLASS = 'highlighted-high-contrast !text-black !bg-ion-light';
    private readonly FILTER_BUTTON_NORMAL_CLASS =
        'highlighted !text-white var-app-light-dark:!text-black btn-bg-secondary orb-app-light-dark:!text-white';
    private readonly FILTER_ICON_HIGH_CONTRAST_CLASS = 'highlighted-high-contrast !filter-none !filter-black';
    private readonly FILTER_ICON_NORMAL_CLASS =
        'highlighted var-app-light-dark:!filter-none orb-app-light-dark:!filter-white';

    get isTimeFilterSet(): boolean {
        if (!this.filterDepartTime?.datetime) return false;

        const plainDateTime = Temporal.PlainDateTime.from(this.filterDepartTime.datetime)
            .toZonedDateTime(this.timezone)
            .toInstant()
            .toString({ timeZone: this.timezone });
        const selectedTimeFilter = Temporal.Instant.from(plainDateTime).toZonedDateTimeISO(this.timezone);

        const selectedTimeFilterDate = selectedTimeFilter.toPlainDateTime().round('minute').toString();
        return selectedTimeFilterDate !== this.today.toPlainDateTime().round('minute').toString();
    }

    private readonly BUTTON_LABEL_SWAP_ROUTES = 'Swap routes';
    private readonly DATE = 'Date';
    private defaultRoutesFilterState = getDefaultValue(this.env.JOURNEY_PLANNER?.FILTERS);
    private timezone: string = this.appStartupService.getTimezone();
    private today: Temporal.ZonedDateTime;
    private fullDateTime = Temporal.Now.zonedDateTimeISO(this.timezone).toPlainDate().toString();
    private currentDateTime = Temporal.Now.zonedDateTimeISO(this.timezone).toString({
        smallestUnit: 'second',
        timeZoneName: 'never'
    });
    private hasPersistedFiltersApplied = false;

    constructor(
        @Inject('ENV') public env: Env,
        private applicationFacade: ApplicationFacade,
        private openTripPlannerFacade: OpenTripPlannerFacade,
        private datePipe: DatePipe,
        private router: Router,
        private translate: TranslateService,
        private route: ActivatedRoute,
        private readonly searchService: SearchService,
        private modalCtrl: ModalController,
        private appStartupService: AppStartupService,
        private otpService: OpenTripPlannerService,
        private analyticsService: AnalyticsService,
        private journeyPlannerFacade: JourneyPlannerFacade
    ) {
        super();

        this.today = Temporal.Now.zonedDateTimeISO(this.timezone);
    }

    ngOnInit() {
        const currentParams = this.searchService.getPickedRouteSearchParams();
        if (currentParams) {
            this.searchService.setPickedRouteSearchParams(null);
        }
        this.openTripPlannerFacade.clearPlanResults();

        this.highContrast$.pipe(takeUntil(this.componentDestroyed$)).subscribe((value) => {
            this.isHighContrast = value;
        });

        this.initSubscriptions();
        this.setFilterMinMaxDate();
        this.checkFilterDepartTime();
        this.checkMinDate();

        this.openTripPlannerFacade.getAllRoutes();

        if (this.env.JOURNEY_PLANNER?.SHOW_SEARCH_HISTORY) {
            this.openTripPlannerFacade.getAllSearchRecentlyJourneys();
        }
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

    setSearchDateAndTime(): void {
        this.analyticsService.eventTrack(TrackedEventAction.FILTER_APPLIED, {
            category: TrackedEventCategory.JOURNEY_PLANNER,
            name: this.DATE
        });

        this.date = Temporal.PlainDate.from(this.filterDepartTime.datetime).toString();
        this.time = Temporal.PlainTime.from(this.filterDepartTime.datetime).toString({ smallestUnit: 'minute' });

        const zonedDt = Temporal.Instant.from(this.filterDepartTime.datetime)
            .toZonedDateTimeISO(this.timezone)
            .toInstant()
            .toString({ timeZone: this.timezone });

        this.openTripPlannerFacade.setSearchDateAndTime(
            this.date,
            this.time,
            this.filterDepartTime.type === 'arriveBy'
        );

        this.searchService.updatePickedRouteSearchParams('searchDateAndTime', {
            date: this.date,
            time: this.time,
            arrive: this.filterDepartTime.type === 'arriveBy'
        });

        this.selectTime.emit(zonedDt);
    }

    toggleRoutesFilter = async (): Promise<void> => {
        this.analyticsService.eventTrack(TrackedEventAction.FILTER_OPENED, {
            category: TrackedEventCategory.JOURNEY_PLANNER,
            name: 'Filter Modal'
        });

        if (this.env.JOURNEY_PLANNER?.FILTERS?.SHOW_FILTERS_IN_MODAL) {
            const modal = await this.modalCtrl.create({
                component: JourneyPlannerFilterModalComponent,
                cssClass: 'journey-planner-filter-modal',
                componentProps: {
                    filter: this.filter,
                    defaultFilter: null
                }
            });

            await modal.present();

            modal.onDidDismiss().then(async ({ data }) => {
                if (data) {
                    if (this.didFiltersChange(data.filter)) {
                        this.routesFilterResultAnalyticsFootprint(data.filter);

                        this.filter = data.filter;
                        this.areRouteFiltersInDefaultState();

                        //THIS IS A TEMPORARY SOLUTION TO SEND IN CORRECT DATA TO OTP UNTIL IT'S IMPLEMENTED IN BE
                        if (this.env?.WMB?.DONT_USE_FOR?.includes('PLAN')) {
                            const modifiedFilters = { ...this.filter };
                            if (this.filter.walkSpeed) {
                                const correctSelector = this.env.JOURNEY_PLANNER.FILTERS.SELECTORS.find(
                                    (selector) => selector.QUERY_PARAMETER === queryParameter.walkSpeed
                                );

                                if (correctSelector.TYPE === 'SLIDER') {
                                    modifiedFilters.walkSpeed =
                                        +correctSelector.VALUES[+this.filter.walkSpeed].VALUE * (1000 / 3600);
                                } else {
                                    modifiedFilters.walkSpeed = +this.filter.walkSpeed * (1000 / 3600);
                                }
                            }
                            if (this.filter.minTransferTime) {
                                const correctSelector = this.env.JOURNEY_PLANNER.FILTERS.SELECTORS.find(
                                    (selector) => selector.QUERY_PARAMETER === queryParameter.minTransferTime
                                );

                                if (correctSelector.TYPE === 'SLIDER') {
                                    modifiedFilters.minTransferTime =
                                        +correctSelector.VALUES[+this.filter.minTransferTime].VALUE * 60;
                                } else {
                                    modifiedFilters.minTransferTime = +this.filter.minTransferTime * (1000 / 3600);
                                }
                            }

                            this.openTripPlannerFacade.setJourneyPlannerFilters(modifiedFilters);
                        } else {
                            this.openTripPlannerFacade.setJourneyPlannerFilters(this.filter);
                        }
                    }
                }
            });
        } else {
            const nativeEl = this.accordionGroup;
            if (nativeEl.value === 'second') {
                nativeEl.value = undefined;
            } else {
                nativeEl.value = 'second';
            }
        }
    };

    toggleSwapRoutes = (): void => {
        this.analyticsService.eventTrack(TrackedEventAction.CLICKED, {
            category: TrackedEventCategory.JOURNEY_PLANNER,
            name: this.BUTTON_LABEL_SWAP_ROUTES
        });
        this.openTripPlannerFacade.swapSearchOriginAndDestination();
        this.journeyPlannerFacade.refreshFavoriteStops();
    };

    getTimeLabel(): string {
        const plainDateTime = Temporal.PlainDateTime.from(this.filterDepartTime.datetime).toString();
        const formattedDate = this.datePipe.transform(
            plainDateTime,
            this.env.PRIMARY_DATE_TIME_FORMAT || this.env.PRIMARY_DATE_FORMAT_NGPIPE
        );

        return (
            (this.filterDepartTime.type == 'arriveBy'
                ? `${this.translate.instant('DATETIME_MODAL_ARRIVE_BY')}: `
                : `${this.translate.instant('DATETIME_MODAL_LEAVE_AFTER')}: `) + formattedDate
        );
    }

    filterChangedHandler(filter) {
        this.filter = filter;
        this.openTripPlannerFacade.setJourneyPlannerFilters(this.filter);
    }

    areRouteFiltersInDefaultState() {
        if (this.filter == null) return false;
        this.routeFiltersHaveChanged = JSON.stringify(this.defaultRoutesFilterState) !== JSON.stringify(this.filter);
        this.updateFilterClasses();
    }

    didFiltersChange(modifiedFilters) {
        if (modifiedFilters == null) return false;
        return JSON.stringify(this.filter) !== JSON.stringify(modifiedFilters);
    }

    onOpenDatetimeModal() {
        this.analyticsService.eventTrack(TrackedEventAction.FILTER_OPENED, {
            category: TrackedEventCategory.JOURNEY_PLANNER,
            label: this.DATE
        });
    }

    private routesFilterResultAnalyticsFootprint(data: any) {
        const objectKeys = Object.keys(data);
        const excludedKeys = ['routes', 'transportModesValue'];

        objectKeys.forEach((key: string) => {
            if (excludedKeys.includes(key)) return;

            this.analyticsService.eventTrack(TrackedEventAction.FILTER_APPLIED, {
                category: TrackedEventCategory.JOURNEY_PLANNER,
                name: `${key} = ${this.flattenToString(data[key])}`
            });
        });
    }

    private flattenToString(input: any): string {
        if (Array.isArray(input)) {
            return input
                .flat(Infinity)
                .map(String)
                .map((s) => s.toLowerCase())
                .join(', ');
        }
        return String(input).toLowerCase();
    }

    private initSubscriptions(): void {
        this.applicationFacade.highContrast$.pipe(takeUntil(this.componentDestroyed$)).subscribe((isContrast) => {
            this.isHighContrast = isContrast;
            this.updateFilterClasses();
        });

        this.otpService
            .getDefaultRouteFilters(getDefaultValue(this.env.JOURNEY_PLANNER?.FILTERS))
            .subscribe((filters) => {
                this.defaultRoutesFilterState = filters;
                if (!this.hasPersistedFiltersApplied || !this.filter) {
                    this.filter = filters;
                }
                this.areRouteFiltersInDefaultState();
            });

        this.routes$.pipe(takeUntil(this.componentDestroyed$)).subscribe((routes) => {
            if (routes) {
                this.routes = routes.map((v) => ({ ...v, selected: true }));
            }
        });

        this.search$.pipe(takeUntil(this.componentDestroyed$)).subscribe((search) => {
            if (search?.filters) {
                this.filter = search.filters;
                this.hasPersistedFiltersApplied = true;
                this.areRouteFiltersInDefaultState();
            }

            if (search?.destination && search?.origin) {
                this.openTripPlannerFacade.addSearchRecentlyJourneys(search);
                this.router.navigate(['../result'], { relativeTo: this.route });
            }

            if (search?.searchDateAndTime?.date) {
                this.date = search?.searchDateAndTime?.date;
                this.time = search?.searchDateAndTime?.time;

                const plainDateTime = Temporal.PlainDate.from(this.date)
                    .toPlainDateTime(Temporal.PlainTime.from(this.time))
                    .toString();

                this.filterDepartTime.datetime = Temporal.ZonedDateTime.from(plainDateTime + `[${this.timezone}]`)
                    .toInstant()
                    .toString({ timeZone: this.timezone });
            }

            if (search?.origin) this.hasOriginSearch = true;
            if (search?.destination) this.hasDestinationSearch = true;
        });
    }

    private setFilterMinMaxDate(): void {
        this.minDateForFilter = this.env.ENABLE_FULL_TIME_SELECTION ? this.fullDateTime : this.currentDateTime;
        this.maxDateForFilter = Temporal.Now.plainDateISO(this.timezone)
            .add({ years: 1 })
            .with({ month: 12, day: 31 })
            .toString();
    }

    private checkFilterDepartTime(): void {
        if (!this.filterDepartTime?.datetime) {
            this.filterDepartTime = {
                datetime: this.today.toInstant().toString({ timeZone: this.timezone }),
                type: 'leaveAfter'
            };
        }
    }

    private checkMinDate(): void {
        if (!this.minDate) {
            this.minDate = this.today.toPlainDateTime().toString();
        }
    }

    updateFilterClasses() {
        if (!this.routeFiltersHaveChanged) {
            this.applyDefaultFilterStyles();
            return;
        }

        if (this.isHighContrast) {
            this.filterButtonClass = this.FILTER_BUTTON_HIGH_CONTRAST_CLASS;
            this.filterIconClass = this.FILTER_ICON_HIGH_CONTRAST_CLASS;
        } else {
            this.filterButtonClass = this.FILTER_BUTTON_NORMAL_CLASS;
            this.filterIconClass = this.FILTER_ICON_NORMAL_CLASS;
        }
    }

    applyDefaultFilterStyles(): void {
        this.filterButtonClass = '';
        this.filterIconClass = '';
    }
}
